import React, { useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

/**
 * 허용 부서 선택.
 *
 * 저장되는 값은 부서명 목록 하나뿐이고, 부문은 하위 부서를 한 번에 켜고 끄는 수단이다.
 * 그래서 부문 체크박스는 스스로 저장되지 않고 소속 부서의 체크 상태만 바꾼다.
 * 하나도 안 고르면 전체 공개다.
 *
 * @param {Array} sectors  DIDAS 부문 목록 (sapGroupCode, sapGroupName)
 * @param {Array} depts    DIDAS 부서 목록 (deptCd, deptName, sapGroupCode)
 * @param {Array} value    선택된 부서명 배열
 * @param {Function} onChange  (부서명 배열) => void
 */
export default function DeptSelect({ sectors = [], depts = [], value = [], onChange }) {
  // 부문 7개만 먼저 보이게 전부 접은 채로 시작한다. 펼친 것만 여기에 담긴다.
  // 접혀 있어도 부문 줄의 '선택수/전체수' 로 어디를 골랐는지는 보인다.
  const [expanded, setExpanded] = useState({});

  const selected = useMemo(() => new Set(value), [value]);

  // 부문 순서대로 묶는다. 어느 부문에도 안 걸리는 부서가 생기면 마지막에 '기타'로 모은다.
  const groups = useMemo(() => {
    const byCode = new Map(sectors.map((s) => [s.sapGroupCode, { key: s.sapGroupCode, label: s.sapGroupName, items: [] }]));
    const etc = { key: '__etc__', label: '기타', items: [] };

    depts.forEach((d) => {
      const group = byCode.get(d.sapGroupCode) || etc;
      group.items.push(d.deptName);
    });

    const list = [...byCode.values()].filter((g) => g.items.length > 0);
    if (etc.items.length) list.push(etc);

    return list;
  }, [sectors, depts]);

  // 조직개편으로 원장에서 사라진 부서가 값에 남아 있을 수 있다.
  // 화면에서 안 보이면 지울 수도 없으므로 따로 모아 보여준다.
  const orphans = useMemo(() => {
    const known = new Set(depts.map((d) => d.deptName));
    return value.filter((name) => !known.has(name));
  }, [depts, value]);

  const emit = (next) => {
    if (onChange) onChange(next);
  };

  const toggleDept = (name) => () => {
    emit(selected.has(name) ? value.filter((v) => v !== name) : [...value, name]);
  };

  // 부문 체크박스는 하위 부서를 통째로 켜거나 끈다. 일부만 켜져 있으면 전부 켜는 쪽으로 간다.
  const toggleSector = (items, allChecked) => () => {
    if (allChecked) {
      const drop = new Set(items);
      emit(value.filter((v) => !drop.has(v)));
    } else {
      const add = items.filter((name) => !selected.has(name));
      emit([...value, ...add]);
    }
  };

  const toggleCollapse = (key) => () => {
    setExpanded({ ...expanded, [key]: !expanded[key] });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <FormLabel>허용 부서</FormLabel>
        <Button size="small" onClick={() => emit([])} disabled={value.length === 0}>
          전체 해제
        </Button>
      </Stack>

      <Typography variant="caption" color={value.length === 0 ? 'primary' : 'text.secondary'} sx={{ display: 'block', mb: 0.5 }}>
        {value.length === 0 ? '선택 안 함 = 모든 부서에 표시' : `${value.length}개 부서에만 표시`}
      </Typography>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 320, overflowY: 'auto' }}>
        {groups.map((group) => {
          const checkedCount = group.items.filter((name) => selected.has(name)).length;
          const allChecked = checkedCount === group.items.length;
          const isOpen = !!expanded[group.key];

          return (
            <Box key={group.key}>
              <Stack direction="row" alignItems="center" sx={{ bgcolor: 'action.hover', pr: 1 }}>
                <Checkbox size="small" checked={allChecked} indeterminate={checkedCount > 0 && !allChecked} onChange={toggleSector(group.items, allChecked)} />
                <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500 }}>
                  {group.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  {checkedCount}/{group.items.length}
                </Typography>
                <IconButton size="small" onClick={toggleCollapse(group.key)}>
                  {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Stack>
              <Collapse in={isOpen}>
                {group.items.map((name) => (
                  <Stack key={name} direction="row" alignItems="center" sx={{ pl: 3 }}>
                    <Checkbox size="small" checked={selected.has(name)} onChange={toggleDept(name)} />
                    <Typography variant="body2">{name}</Typography>
                  </Stack>
                ))}
              </Collapse>
            </Box>
          );
        })}

        {orphans.length > 0 && (
          <Box>
            <Stack direction="row" alignItems="center" sx={{ bgcolor: 'error.light', pl: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                원장에 없는 부서
              </Typography>
            </Stack>
            {orphans.map((name) => (
              <Stack key={name} direction="row" alignItems="center" sx={{ pl: 3 }}>
                <Checkbox size="small" checked onChange={toggleDept(name)} />
                <Typography variant="body2">{name}</Typography>
              </Stack>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
